import { forwardRef, useRef, type InputHTMLAttributes, type SelectHTMLAttributes } from 'react'

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label?: string
  /** When given, a × button clears the field whenever it holds text. */
  onClear?: () => void
}

/** Instrument-style input: no box, just a bottom rule that highlights on focus. */
export const Field = forwardRef<HTMLInputElement, Props>(function Field(
  { label, className = '', onClear, ...rest }, ref,
) {
  const inner = useRef<HTMLInputElement | null>(null)
  const setRef = (el: HTMLInputElement | null) => {
    inner.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }
  const showClear = Boolean(onClear) && rest.value != null && String(rest.value) !== ''
  return (
    <label className="block">
      {label && <span className="label mb-1.5 block">{label}</span>}
      <div className="relative">
        <input
          ref={setRef}
          className={`
            w-full bg-transparent border-b border-ink-500
            font-mono text-[13px] text-paper
            py-1.5 placeholder:text-paper-trace
            focus:outline-none focus:border-amber-glow
            transition-colors duration-150
            ${showClear ? 'pr-6' : ''}
            ${className}
          `}
          {...rest}
        />
        {showClear && (
          <button
            type="button"
            aria-label="Clear"
            title="Clear"
            className="absolute right-0 top-1/2 -translate-y-1/2 px-1 text-paper-dim hover:text-paper text-lg leading-none"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onClear?.()
              inner.current?.focus()
            }}
          >×</button>
        )}
      </div>
    </label>
  )
})

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string
  options: Array<{ value: string; label: string }>
}

export function Select({ label, options, className = '', ...rest }: SelectProps) {
  return (
    <label className="block">
      {label && <span className="label mb-1.5 block">{label}</span>}
      <div className="relative">
        <select
          className={`
            w-full appearance-none bg-ink-700 border border-ink-500
            font-mono text-[12px] text-paper uppercase tracking-label
            py-1.5 pl-3 pr-8 cursor-pointer
            focus:outline-none focus:border-amber-glow
            ${className}
          `}
          {...rest}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-paper-faint pointer-events-none">
          ▾
        </span>
      </div>
    </label>
  )
}
